/**
 * Audit Storage Service — Saves and loads audit results and chat history from Supabase.
 * Tables: card_audit_results, chat_history
 */

import { supabase } from "@/lib/supabaseClient";

// ── Types ──

export interface SavedAuditResult {
  id: string;
  user_id: string;
  domain: string;
  card_id: string;
  shortcut_id: string;
  edge_function: string;
  region: string;
  form_data: Record<string, any>;
  structured_data: Record<string, any>;
  analysis_text: string;
  recommendations: string[];
  processing_time: number;
  sources: Record<string, boolean>;
  created_at: string;
}

export interface ChatHistoryRecord {
  id: string;
  user_id: string;
  card_id: string;
  tab_id: string;
  domain: string | null;
  messages: any[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

// ── Audit Results ──

/**
 * Save an audit result to Supabase
 */
export async function saveAuditResult(params: {
  userId?: string;
  domain: string;
  cardId: string;
  shortcutId: string;
  edgeFunction: string;
  region: string;
  result: Record<string, any>;
}): Promise<string | null> {
  try {
    if (!params.userId) {
      console.warn("[AuditStorage] No authenticated user — skipping save");
      return null;
    }

    const { data, error } = await supabase
      .from("card_audit_results")
      .insert({
        user_id: params.userId,
        domain: params.domain,
        card_id: params.cardId,
        shortcut_id: params.shortcutId,
        edge_function: params.edgeFunction,
        region: params.region,
        structured_data: params.result,
        analysis_text: params.result.analysis || "",
        recommendations: params.result.recommendations || [],
        processing_time: params.result.processingTime || 0,
        sources: params.result.sources || {},
      })
      .select("id")
      .single();

    if (error) {
      console.error("[AuditStorage] Save error:", error.message);
      return null;
    }

    console.log(`✅ [AuditStorage] Saved audit ${data.id} for ${params.domain} (${params.shortcutId})`);
    return data.id;
  } catch (err) {
    console.error("[AuditStorage] Unexpected error:", err);
    return null;
  }
}

/**
 * Load the latest audit results for a card + domain
 */
export async function loadLatestAudits(params: {
  userId?: string;
  cardId: string;
  domain?: string;
  limit?: number;
}): Promise<SavedAuditResult[]> {
  try {
    if (!params.userId) return [];

    let query = supabase
      .from("card_audit_results")
      .select("*")
      .eq("user_id", params.userId)
      .eq("card_id", params.cardId)
      .order("created_at", { ascending: false })
      .limit(params.limit || 10);

    if (params.domain) {
      query = query.eq("domain", params.domain);
    }

    const { data, error } = await query;

    if (error) {
      console.error("[AuditStorage] Load error:", error.message);
      return [];
    }

    return (data || []) as SavedAuditResult[];
  } catch (err) {
    console.error("[AuditStorage] Unexpected error:", err);
    return [];
  }
}

/**
 * Load the latest audit for a specific shortcut type
 */
export async function loadLatestAuditByShortcut(params: {
  userId?: string;
  cardId: string;
  shortcutId: string;
  domain?: string;
  reportId?: string | null;
}): Promise<SavedAuditResult | null> {
  try {
    if (!params.userId) return null;

    let query = supabase
      .from("card_audit_results")
      .select("*")
      .eq("user_id", params.userId)
      .eq("card_id", params.cardId)
      .eq("shortcut_id", params.shortcutId)
      .order("scored_at", { ascending: false, nullsFirst: false })
      .order("created_at", { ascending: false })
      .limit(1);

    if (params.domain) {
      query = query.eq("domain", params.domain);
    }

    if (params.reportId) {
      query = query.eq("report_id", params.reportId);
    }

    const { data, error } = await query;

    if (error || !data || data.length === 0) return null;

    return data[0] as SavedAuditResult;
  } catch (err) {
    console.error("[AuditStorage] Unexpected error:", err);
    return null;
  }
}

// ── Chat History ──

/**
 * Save or update chat history for a card tab
 */
export async function saveChatHistory(params: {
  userId?: string;
  cardId: string;
  tabId: string;
  domain?: string;
  messages: any[];
}): Promise<string | null> {
  try {
    if (!params.userId) return null;

    // Check if active chat exists for this card/tab
    const { data: existing } = await supabase
      .from("chat_history")
      .select("id")
      .eq("user_id", params.userId)
      .eq("card_id", params.cardId)
      .eq("tab_id", params.tabId)
      .eq("is_active", true)
      .limit(1);

    if (existing && existing.length > 0) {
      // Update existing
      const { error } = await supabase
        .from("chat_history")
        .update({
          messages: params.messages,
          domain: params.domain || null,
        })
        .eq("id", existing[0].id);

      if (error) {
        console.error("[ChatStorage] Update error:", error.message);
        return null;
      }
      return existing[0].id;
    } else {
      // Insert new
      const { data, error } = await supabase
        .from("chat_history")
        .insert({
          user_id: params.userId,
          card_id: params.cardId,
          tab_id: params.tabId,
          domain: params.domain || null,
          messages: params.messages,
          is_active: true,
        })
        .select("id")
        .single();

      if (error) {
        console.error("[ChatStorage] Insert error:", error.message);
        return null;
      }
      return data.id;
    }
  } catch (err) {
    console.error("[ChatStorage] Unexpected error:", err);
    return null;
  }
}

/**
 * Load chat history for a card tab
 */
export async function loadChatHistory(params: {
  userId?: string;
  cardId: string;
  tabId: string;
}): Promise<any[] | null> {
  try {
    if (!params.userId) return null;

    const { data, error } = await supabase
      .from("chat_history")
      .select("messages")
      .eq("user_id", params.userId)
      .eq("card_id", params.cardId)
      .eq("tab_id", params.tabId)
      .order("updated_at", { ascending: false })
      .limit(1);

    if (error || !data || data.length === 0) return null;

    return data[0].messages;
  } catch (err) {
    console.error("[ChatStorage] Unexpected error:", err);
    return null;
  }
}

/**
 * Start a new chat (deactivate old, create fresh)
 */
export async function startNewChat(params: {
  userId?: string;
  cardId: string;
  tabId: string;
}): Promise<void> {
  try {
    if (!params.userId) return;

    // Deactivate old chats for this card/tab
    await supabase
      .from("chat_history")
      .update({ is_active: false })
      .eq("user_id", params.userId)
      .eq("card_id", params.cardId)
      .eq("tab_id", params.tabId)
      .eq("is_active", true);

  } catch (err) {
    console.error("[ChatStorage] Unexpected error:", err);
  }
}

